import { ReactNode } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { ILocationGroupAvailability } from "../../models/LocationGroup";
import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardSession,
} from "../../models/ProductionDashboard";
import { buildFlowLane, flowStagedVehicleIds } from "../../utils/flowLaneColumns";
import { plannedTodayCount } from "../../utils/productionDashboardView";
import { IRackingSummary } from "../../utils/tvLocationTiles";
import TvLineLane from "./TvLineLane";
import TvOverlayShell, { TvDueNextStrip } from "./TvOverlayShell";

interface TvLineOverlayProps {
  bays: IProductionDashboardBay[];
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  busyVehicleIds?: Set<string>;
  triages: IProductionDashboardSession[];
  depollutions: IProductionDashboardSession[];
  completedTodayCount: number;
  locationGroups: ILocationGroupAvailability[];
  /** Yard sections that get a tile of their own (the "ProductionDashboard:TvLocationTiles" setting). */
  maxLocationTiles: number;
  /** Omitted, or with no racking locations, the Racking tile is not shown. */
  racking?: IRackingSummary;
  /** With a stage in the flow its sessions show in the lanes, so the footer count is dropped. */
  triageInFlow: boolean;
  depollutionInFlow: boolean;
  onClose: () => void;
}

function TvHeadlineTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-800 bg-gray-900 px-5 py-2 min-w-[7rem]">
      <span className="text-sm font-semibold uppercase tracking-widest text-gray-400 whitespace-nowrap">
        {label}
      </span>
      <span className="text-4xl font-semibold leading-none tabular-nums mt-1">{value}</span>
      {sub && <span className="text-xs text-gray-500 tabular-nums mt-1">{sub}</span>}
    </div>
  );
}

// The Line view blown up for a screen on the wall: headline counts along the top, then one lane
// per dismantling line with the stations left to right. Vehicle placement comes from the same
// function as the Assembly Flow below it, so the two can never disagree.
export default function TvLineOverlay({
  bays,
  assemblies,
  now,
  busyVehicleIds,
  triages,
  depollutions,
  completedTodayCount,
  locationGroups,
  maxLocationTiles,
  racking,
  triageInFlow,
  depollutionInFlow,
  onClose,
}: TvLineOverlayProps) {
  const { t } = useTranslation("production");

  const stagedVehicleIds = flowStagedVehicleIds(bays);
  const lanes = bays.map((bay) => ({
    bay,
    lane: buildFlowLane(bay, assemblies, triages, depollutions, stagedVehicleIds, busyVehicleIds),
  }));

  const plannedToday = plannedTodayCount(bays);

  // The header row has to share its width with the clock, so only the first few yard sections get a
  // tile of their own; the Total still sums every section.
  const shownGroups = locationGroups.slice(0, maxLocationTiles);
  const locationTotal = locationGroups.reduce((n, g) => n + g.vehicleCount, 0);

  const footerCounts = [
    !triageInFlow ? `${t("Triage")} ${triages.length}` : null,
    !depollutionInFlow ? `${t("Depollution")} ${depollutions.length}` : null,
  ].filter(Boolean);

  return (
    <TvOverlayShell
      now={now}
      onClose={onClose}
      headerContent={
        <>
          <TvHeadlineTile
            label={`${t("Planned")} | ${t("Actual")}`}
            value={
              <>
                {plannedToday}
                <span className="text-gray-600 mx-2">|</span>
                {completedTodayCount}
              </>
            }
          />
          {shownGroups.map((group) => (
            <TvHeadlineTile
              key={group.locationGroupId}
              label={group.name}
              value={group.vehicleCount}
              sub={group.capacity > 0 ? `/ ${group.capacity}` : undefined}
            />
          ))}
          {locationGroups.length > 0 && <TvHeadlineTile label={t("Total")} value={locationTotal} />}
          {racking && racking.locationCount > 0 && (
            <TvHeadlineTile
              label={t("Racking")}
              value={racking.used}
              sub={`/ ${racking.capacity} · ${t("{{count}} free", { count: racking.available })}`}
            />
          )}
        </>
      }
      footer={
        <TvDueNextStrip
          bays={bays}
          trailing={footerCounts.length > 0 ? footerCounts.join(" · ") : undefined}
        />
      }
    >
      {bays.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-2xl text-gray-400">
          {t("No dismantling lines configured.")}
        </div>
      ) : assemblies.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-2xl text-gray-400">
          {t("No assemblies configured.")}
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto">
          {lanes.map(({ bay, lane }) => (
            <TvLineLane key={bay.id} bay={bay} lane={lane} now={now} />
          ))}
        </div>
      )}
    </TvOverlayShell>
  );
}
